import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';

interface AddActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (name: string, startTime: string, endTime: string, notes?: string, calories?: number) => void;
  activity?: { id: string; name: string; startTime: Date; endTime: Date; notes?: string; calories?: number } | null;
}

export function AddActivityDialog({ open, onOpenChange, onSuccess, activity }: AddActivityDialogProps) {
  const { t, dir } = useLanguage();
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [endTime, setEndTime] = useState(format(new Date(Date.now() + 3600000), "yyyy-MM-dd'T'HH:mm"));
  const [notes, setNotes] = useState('');
  const [calories, setCalories] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activity) {
      setName(activity.name);
      setStartTime(format(activity.startTime, "yyyy-MM-dd'T'HH:mm"));
      setEndTime(format(activity.endTime, "yyyy-MM-dd'T'HH:mm"));
      setNotes(activity.notes || '');
      setCalories(activity.calories?.toString() || '');
    } else {
      setName('');
      setStartTime(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
      setEndTime(format(new Date(Date.now() + 3600000), "yyyy-MM-dd'T'HH:mm"));
      setNotes('');
      setCalories('');
    }
  }, [activity, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      return;
    }

    setLoading(true);
    try {
      const caloriesNum = calories ? parseFloat(calories) : undefined;
      onSuccess(name, startTime, endTime, notes, caloriesNum);
      handleClose();
    } catch (error) {
      console.error('Error submitting activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!activity) {
      setName('');
      setStartTime(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
      setEndTime(format(new Date(Date.now() + 3600000), "yyyy-MM-dd'T'HH:mm"));
      setNotes('');
      setCalories('');
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} dir={dir}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {activity ? t('activities.editActivity') || 'Edit Activity' : t('activities.addActivity') || 'Log Activity'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('activities.activityName') || 'Activity Name'}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('activities.activityNamePlaceholder') || 'e.g., Morning Jog'}
              required
              dir={dir}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">{t('activities.startTime') || 'Start Time'}</Label>
              <Input
                id="startTime"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime">{t('activities.endTime') || 'End Time'}</Label>
              <Input
                id="endTime"
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="calories">{t('activities.calories') || 'Calories (Optional)'}</Label>
            <Input
              id="calories"
              type="number"
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              placeholder={t('activities.caloriesPlaceholder') || 'e.g., 300'}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('activities.notes') || 'Notes (Optional)'}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('activities.notesPlaceholder') || 'Add any notes about this activity...'}
              rows={3}
              dir={dir}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading
                ? (t('common.saving') || 'Saving...')
                : (activity ? t('common.save') || 'Save' : t('common.add') || 'Add')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

